import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FabricObjectWithSrc } from '../../../shared/Helpers';
import { ShapePoint } from '../Annotation/annotation.types';
import { mapRawToFabricImages } from './Helpers';

type CanvasSize = {
  height: number;
  width: number;
};

export type Overlap = {
  shape: { x: number; y: number }[];
  isActive: boolean;
};

type State = {
  height: number;
  width: number;
  image: Nullable<FabricObjectWithSrc>;
  loaded: boolean;
  loading: boolean;
  activeShape: ShapePoint[];
  activeShapeId: string;
  imageScale: number;
  scaledOverlapping: Overlap[];
  bottomOverplapping: { x: number; y: number }[][];
  createAnnotationCanceled: boolean;
  shouldZoomToFinding: boolean;
  saveFinding: boolean;

  polygonMode: boolean;
  rectangleMode: boolean;
  pickUnselect: boolean;
  toggleAllUserFindingsVisibility: boolean;
  toggleMLFindingsVisibility: boolean;
  toggleOverlappingVisibility: boolean;
  toggleOverlayVisibility: boolean;
  rangeFilterOpened: boolean;
};

let initialState: State = {
  height: 500,
  width: 500,
  image: null,
  loaded: false,
  loading: false,
  activeShape: [],
  imageScale: 1,
  activeShapeId: '',
  scaledOverlapping: [],
  bottomOverplapping: [],
  createAnnotationCanceled: false,
  shouldZoomToFinding: true,
  saveFinding: false,

  polygonMode: false,
  rectangleMode: false,
  pickUnselect: true,
  toggleAllUserFindingsVisibility: true,
  toggleMLFindingsVisibility: true,
  toggleOverlappingVisibility: true,
  toggleOverlayVisibility: true,
  rangeFilterOpened: false
};

const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    resetState(state) {
      return { ...initialState, height: state.height, width: state.width };
    },
    setCanvasSize(state, action: PayloadAction<CanvasSize>) {
      const { height, width } = action.payload;
      state.height = height;
      state.width = width;
    },
    imageSelected(state, action: PayloadAction<any>) {
      const rawImage = action.payload;

      const rawImages = [rawImage];
      const mappedData = mapRawToFabricImages({
        rawImages,
        isStitched: false,
        stitchingManipulationRules: {},
        canvasHeight: state.height,
        canvasWidth: state.width
      });

      state.imageScale = state.width / rawImage.width;

      state.loaded = false;
      state.image = mappedData.fabricImages[0];
    },
    imageLoaded(state) {
      state.loaded = true;
      state.loading = false;
    },
    imageUnloaded(state) {
      state.image = null;
      state.loaded = false;
    },
    imageLoading(state) {
      state.loading = true;
      state.loaded = false;
      state.image = null;
    },
    setActiveShape(state, action: PayloadAction<{ shape: ShapePoint[]; imageScale: number; id: string }>) {
      const { shape, imageScale, id } = action.payload;
      state.activeShape = shape;
      state.imageScale = imageScale;
      state.activeShapeId = id;
    },
    setScaledOverlapping(state, action: PayloadAction<Overlap[]>) {
      state.scaledOverlapping = action.payload;
      state.bottomOverplapping = state.scaledOverlapping.filter(x => x.isActive).map(x => x.shape);
    },
    annotationCreationCanceled(state, action: PayloadAction<boolean>) {
      state.createAnnotationCanceled = action.payload;
    },
    setShouldZoomToFinding(state, action: PayloadAction<boolean>) {
      state.shouldZoomToFinding = action.payload;
    },
    setSaveFinding(state, action: PayloadAction<boolean>) {
      state.saveFinding = action.payload;
    },
    generatePolygon(state, action: PayloadAction<boolean>) {
      state.polygonMode = action.payload;
      state.rectangleMode = false;
      state.pickUnselect = false;
    },
    generateRectangle(state, action: PayloadAction<boolean>) {
      state.rectangleMode = action.payload;
      state.polygonMode = false;
      state.pickUnselect = false;
    },
    generatePickUnselected(state, action: PayloadAction<boolean>) {
      state.pickUnselect = action.payload;
      state.polygonMode = false;
      state.rectangleMode = false;
    },
    toggleAllUserFindingsVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.toggleAllUserFindingsVisibility = action.payload;
    },
    toggleMLFindingsVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.toggleMLFindingsVisibility = action.payload;
    },
    toggleAllFindingsVisibilityChanged(state) {
      if (!state.rangeFilterOpened) {
        const visibility = !state.toggleAllUserFindingsVisibility;

        state.toggleAllUserFindingsVisibility = visibility;
        state.toggleMLFindingsVisibility = visibility;
      }
    },
    toggleOverlappingVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.toggleOverlappingVisibility = action.payload;
    },
    toggleOverlayVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.toggleOverlayVisibility = action.payload;
    },
    setRangeFilterOpened(state, action: PayloadAction<boolean>) {
      state.rangeFilterOpened = action.payload;
    }
  }
});

export const {
  setCanvasSize,
  imageSelected,
  imageLoaded,
  setActiveShape,
  imageUnloaded,
  imageLoading,
  resetState,
  setScaledOverlapping,
  annotationCreationCanceled,
  setShouldZoomToFinding,
  setSaveFinding,
  generatePolygon,
  generateRectangle,
  generatePickUnselected,
  toggleAllUserFindingsVisibilityChanged,
  toggleMLFindingsVisibilityChanged,
  toggleAllFindingsVisibilityChanged,
  toggleOverlappingVisibilityChanged,
  toggleOverlayVisibilityChanged,
  setRangeFilterOpened
} = canvasSlice.actions;

export default canvasSlice.reducer;
